import { create } from 'zustand'
import apiClient from '../api/apiClient'

interface CustomerStore {
  customers: string[]
  selectedCustomer: string | 'all'
  isLoading: boolean
  error: string | null
  isSidebarCollapsed: boolean
  setSelectedCustomer: (customer: string | 'all') => void
  setSidebarCollapsed: (collapsed: boolean) => void
  loadCustomers: () => Promise<void>
}

export const useCustomerStore = create<CustomerStore>((set) => ({
  customers: [],
  selectedCustomer: 'all',
  isLoading: false,
  error: null,
  isSidebarCollapsed: false,

  setSelectedCustomer: (customer) => set({ selectedCustomer: customer }),
  setSidebarCollapsed: (collapsed) => set({ isSidebarCollapsed: collapsed }),

  loadCustomers: async () => {
    set({ isLoading: true, error: null })

    try {
      const response = await apiClient.get<any[]>('/api/customers')
      const data = response.data

      // 날짜 파싱 함수: null/N/A는 Infinity (맨 아래)로 처리
      const parseDate = (dateValue: any): Date => {
        if (!dateValue || dateValue === 'N/A') return new Date(Infinity)
        if (Array.isArray(dateValue)) {
          return new Date(dateValue[0], dateValue[1] - 1, dateValue[2])
        }
        if (typeof dateValue === 'string' && dateValue.startsWith('[')) {
          const parts = JSON.parse(dateValue)
          return new Date(parts[0], parts[1] - 1, parts[2])
        }
        return new Date(dateValue)
      }

      // 갱신예상일과 계약만료일 중 더 빠른 날짜 기준 정렬
      // 계약 정보가 없는 고객(null/N/A)은 맨 아래로 정렬
      const sortedData = data.sort((a: any, b: any) => {
        const aRenewalDate = parseDate(a.balanceExhaustionDate)
        const aExpiryDate = parseDate(a.expiryDate)
        const bRenewalDate = parseDate(b.balanceExhaustionDate)
        const bExpiryDate = parseDate(b.expiryDate)

        // 두 날짜 중 더 빠른 날짜 선택
        const aEarliestDate = aRenewalDate < aExpiryDate ? aRenewalDate : aExpiryDate
        const bEarliestDate = bRenewalDate < bExpiryDate ? bRenewalDate : bExpiryDate

        // Infinity끼리 비교 시 0 반환 (순서 유지)
        if (!isFinite(aEarliestDate.getTime()) && !isFinite(bEarliestDate.getTime())) {
          return 0
        }

        return aEarliestDate.getTime() - bEarliestDate.getTime()
      })

      // Extract customerName from sorted customer objects
      const customerNames = sortedData.map((customer: any) => customer.customerName)
      set({ customers: customerNames, isLoading: false })
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : '고객 목록을 불러오는데 실패했습니다.'
      set({ error: errorMessage, isLoading: false })
      console.error('Failed to load customers:', error)
    }
  },
}))
